import * as React from 'react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import V2Shell from './components/V2Shell';
import useCreateActivation from '@/src/Activations/components/hooks/useCreateActivation';
import {
    IconHome,
    IconActivations,
    IconProduction,
    IconPlus,
} from './components/icons';

/* App-level chrome: org crumb + the app dock. Targets without a v2
   screen bridge to the classic app. */

export default function V2AppChrome() {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { createActivation } = useCreateActivation();
    const [createOpen, setCreateOpen] = useState(false);
    const [fields, setFields] = useState({
        name: '',
        date: moment().format('YYYY-MM-DD'),
        location: '',
    });
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState(false);

    const submitCreate = async () => {
        if (!fields.name.trim() || !fields.date || creating) return;
        setCreating(true);
        setCreateError(false);
        const created = await createActivation({
            name: fields.name.trim(),
            date: fields.date,
            location: fields.location.trim(),
        }).catch(() => null);
        setCreating(false);
        if (created?.id) {
            setCreateOpen(false);
            setFields({
                name: '',
                date: moment().format('YYYY-MM-DD'),
                location: '',
            });
            navigate(`/v2/activations/${created.id}`);
        } else {
            setCreateError(true);
        }
    };

    const section = pathname.startsWith('/v2/activations')
        ? 'Activations'
        : pathname === '/v2/devices'
          ? 'Devices'
          : pathname === '/v2/playground'
            ? 'Playground'
            : 'Home';
    /* Devices left the dock (org-level; lives in the org menu) */

    return (
        <V2Shell
            switcher={{
                label: section,
                items: [
                    {
                        key: 'home',
                        label: 'Home',
                        active: section === 'Home',
                        onClick: () => navigate('/v2'),
                    },
                    {
                        key: 'activations',
                        label: 'Activations',
                        active: section === 'Activations',
                        onClick: () => navigate('/v2/activations'),
                    },
                    {
                        key: 'jobs',
                        label: 'Jobs',
                        onClick: () => navigate('/v2/jobs'),
                    },
                ],
            }}
            items={[
                {
                    key: 'home',
                    title: 'Home',
                    icon: <IconHome />,
                    active: pathname === '/v2',
                    onClick: () => navigate('/v2'),
                },
                {
                    key: 'jobs',
                    title: 'Jobs',
                    icon: <IconProduction />,
                    onClick: () => navigate('/v2/jobs'),
                },
                {
                    key: 'activations',
                    title: 'Activations',
                    icon: <IconActivations />,
                    active: pathname.startsWith('/v2/activations'),
                    onClick: () => navigate('/v2/activations'),
                },
            ]}
            actions={[
                {
                    key: 'new',
                    title: 'New activation',
                    icon: <IconPlus />,
                    onClick: () => setCreateOpen(true),
                },
            ]}
        >
            {createOpen && (
                <div
                    className="psv2 fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
                    onClick={() => setCreateOpen(false)}
                >
                    <div
                        className="w-full max-w-[400px] rounded-[15px] border border-border bg-background p-5 shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') setCreateOpen(false);
                        }}
                    >
                        <div className="ps-label mb-3.5">New activation</div>
                        <label className="mb-1.5 block text-[12px] font-semibold text-muted-foreground">
                            Name
                        </label>
                        <input
                            autoFocus
                            className="ps-input mb-3.5 w-full text-[13.5px]"
                            placeholder="20260815 — Client Event"
                            value={fields.name}
                            onChange={(e) =>
                                setFields((f) => ({
                                    ...f,
                                    name: e.target.value,
                                }))
                            }
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') submitCreate();
                            }}
                        />
                        <div className="mb-4 flex gap-5">
                            <div>
                                <label className="mb-1.5 block text-[12px] font-semibold text-muted-foreground">
                                    Date
                                </label>
                                <input
                                    type="date"
                                    className="ps-input text-[13px]"
                                    value={fields.date}
                                    onChange={(e) =>
                                        setFields((f) => ({
                                            ...f,
                                            date: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                            <div className="min-w-0 flex-1">
                                <label className="mb-1.5 block text-[12px] font-semibold text-muted-foreground">
                                    Location{' '}
                                    <span className="font-normal text-faint">
                                        — optional
                                    </span>
                                </label>
                                <input
                                    className="ps-input w-full text-[13px]"
                                    placeholder="Venue, city"
                                    value={fields.location}
                                    onChange={(e) =>
                                        setFields((f) => ({
                                            ...f,
                                            location: e.target.value,
                                        }))
                                    }
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter')
                                            submitCreate();
                                    }}
                                />
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                disabled={
                                    creating ||
                                    !fields.name.trim() ||
                                    !fields.date
                                }
                                onClick={submitCreate}
                                className="cursor-pointer rounded-full border-0 bg-brand px-5 py-2 font-[inherit] text-[13px] font-semibold text-white disabled:opacity-50"
                            >
                                {creating ? 'Creating…' : 'Create activation'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setCreateOpen(false)}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-muted-foreground"
                            >
                                Cancel
                            </button>
                            {createError && (
                                <span className="text-[12px] font-semibold text-attention">
                                    Create failed
                                </span>
                            )}
                        </div>
                        <p className="mt-3 text-[11px] text-faint">
                            A gallery is created with it. Set up sharing,
                            capture, and processing from the event's dock.
                        </p>
                    </div>
                </div>
            )}
        </V2Shell>
    );
}
